import React, { useState, useEffect } from 'react'
import { format, isToday, isTomorrow, addDays } from 'date-fns'

const EventAlerts = () => {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchRadius, setSearchRadius] = useState(5) // miles
  const [userLocation, setUserLocation] = useState(null)

  // Mock event data for demo - in production this would come from Eventbrite API
  const mockEvents = [
    {
      id: 1,
      name: 'Downtown Food Festival',
      date: new Date(),
      location: 'Downtown Plaza',
      attendees: 2500,
      category: 'Food & Drink',
      distance: 0.8,
      coordinates: { lat: 37.7849, lng: -122.4094 }
    },
    {
      id: 2,
      name: 'Tech Conference 2024',
      date: addDays(new Date(), 1),
      location: 'Convention Center',
      attendees: 1200,
      category: 'Business',
      distance: 1.2,
      coordinates: { lat: 37.7749, lng: -122.4194 }
    },
    {
      id: 3,
      name: 'Saturday Farmers Market',
      date: addDays(new Date(), 2),
      location: 'City Park',
      attendees: 800,
      category: 'Community',
      distance: 2.1,
      coordinates: { lat: 37.7649, lng: -122.4294 }
    },
    {
      id: 4,
      name: 'Live Music Concert',
      date: addDays(new Date(), 3),
      location: 'Amphitheater',
      attendees: 3000,
      category: 'Music',
      distance: 3.5,
      coordinates: { lat: 37.7549, lng: -122.4394 }
    }
  ]

  useEffect(() => {
    // Get user location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          })
        },
        (error) => {
          console.log('Geolocation error:', error)
          // Default to San Francisco
          setUserLocation({ lat: 37.7749, lng: -122.4194 })
        }
      )
    }
  }, [])

  useEffect(() => {
    if (userLocation) {
      fetchNearbyEvents()
    }
  }, [userLocation, searchRadius])

  const fetchNearbyEvents = async () => {
    setLoading(true)
    try {
      // Filter mock events by distance
      const nearbyEvents = mockEvents.filter(event => event.distance <= searchRadius)
      setEvents(nearbyEvents)
    } catch (error) {
      console.error('Error fetching events:', error)
    } finally {
      setLoading(false)
    }
  }

  const getEventIcon = (category) => {
    const icons = {
      'Food & Drink': '🍕',
      'Business': '💼',
      'Community': '🏘️',
      'Music': '🎵',
      'Sports': '⚽',
      'Arts': '🎨'
    }
    return icons[category] || '📅'
  }

  const getDateLabel = (date) => {
    if (isToday(date)) return 'Today'
    if (isTomorrow(date)) return 'Tomorrow'
    return format(date, 'MMM dd')
  }

  const getPriorityScore = (event) => {
    // Higher score = better opportunity
    let score = 0
    score += Math.min(event.attendees / 100, 50) // Max 50 points for attendees
    score += Math.max(0, 10 - event.distance) // Closer is better, max 10 points
    if (event.category === 'Food & Drink') score += 20 // Food events are premium
    if (isToday(event.date) || isTomorrow(event.date)) score += 15 // Soon = urgent
    return Math.round(score)
  }

  const sortedEvents = events
    .map(event => ({ ...event, priority: getPriorityScore(event) }))
    .sort((a, b) => b.priority - a.priority)

  return (
    <div className="event-alerts">
      <div className="event-alerts-header">
        <h2>🎉 Event Opportunities</h2>
        <div className="search-controls">
          <label>
            Search radius: 
            <select 
              value={searchRadius} 
              onChange={(e) => setSearchRadius(Number(e.target.value))}
            >
              <option value={2}>2 miles</option>
              <option value={5}>5 miles</option>
              <option value={10}>10 miles</option>
              <option value={20}>20 miles</option>
            </select>
          </label>
          <button onClick={fetchNearbyEvents} disabled={loading}>
            {loading ? 'Searching...' : 'Refresh Events'}
          </button>
        </div>
      </div>

      {loading && (
        <div className="loading-state">
          <div className="spinner">🔄</div>
          <p>Finding events near you...</p>
        </div>
      )}

      <div className="events-grid">
        {sortedEvents.map(event => (
          <div key={event.id} className="event-card">
            <div className="event-header">
              <div className="event-icon">{getEventIcon(event.category)}</div>
              <div className="event-priority">
                <span className="priority-score">{event.priority}</span>
                <small>Priority</small>
              </div>
            </div>
            
            <h3 className="event-name">{event.name}</h3>
            <div className="event-details">
              <div className="event-date">
                📅 {getDateLabel(event.date)} • {format(event.date, 'h:mm a')}
              </div>
              <div className="event-location">
                📍 {event.location} • {event.distance} mi away
              </div>
              <div className="event-attendees">
                👥 ~{event.attendees.toLocaleString()} expected attendees
              </div>
            </div>

            <div className="event-recommendation">
              {event.priority >= 60 && (
                <div className="rec-high">🔥 High Priority - Great opportunity!</div>
              )}
              {event.priority >= 40 && event.priority < 60 && (
                <div className="rec-medium">⭐ Worth Considering</div>
              )}
              {event.priority < 40 && (
                <div className="rec-low">📝 Low Priority</div>
              )}
            </div>

            <div className="event-actions">
              <button className="btn-primary">Add to Schedule</button>
              <button className="btn-secondary">Get Directions</button>
            </div>
          </div>
        ))}
      </div>

      {events.length === 0 && !loading && (
        <div className="empty-events">
          <div className="empty-icon">🔍</div>
          <h3>No events found</h3>
          <p>Try expanding your search radius or check back later for new events.</p>
        </div>
      )}

      <div className="event-tips">
        <h3>💡 Event Tips</h3>
        <ul>
          <li><strong>Food & Drink events:</strong> Highest priority - your direct target market</li>
          <li><strong>Large crowds:</strong> More potential customers, but also more competition</li>
          <li><strong>Business events:</strong> Lunch crowds with higher spending power</li>
          <li><strong>Weekend events:</strong> Families and leisure spending</li>
        </ul>
      </div>
    </div>
  )
}

export default EventAlerts